"use client";

import React, { useRef, Dispatch, SetStateAction, useTransition } from "react";
import {
  ToggleStateProvider,
  ToggleFlipIcon,
  Collapse,
  ListItem,
  Flex,
  ListItemText,
  Grid,
  Checkbox,
  List,
  Button,
} from "@costar/theme-ui";
import { IconClose } from "@costar/theme-ui-icons/csg/IconClose";
import {
  DateRange,
  ResolveRangeDateText,
  TimeFrameDropDown,
  TimeFrameDropdownProps,
} from "@costar/date-picker";
import "@costar/date-picker/dist/datepicker.min.css";
import type { DateRangeISOString } from "@costar/date-picker";
import { useRouter } from "next/navigation";
import { buildSearchURL } from "./search-page-utils";
import {
  ClientTrackingEvent,
  trackingDetails,
} from "../helpers/event-tracking-helpers";
import { trackEventApi } from "../common/hooks/track-event-api";
import { usePathname } from "next/navigation";

interface CheckboxOption {
  id: string;
  value: string;
  name: string;
  checked: boolean;
  disabled: boolean;
}

interface IThematiquesFilterProps {
  thematiquesLabel: string;
  categoryOptions: CheckboxOption[];
  onChange?: () => void;
}

interface ICategoriesFilterProps {
  categoriesLabel: string;
  labelOptions: CheckboxOption[];
  onChange?: () => void;
}

type ICheckboxListProps = {
  name: string;
  options: CheckboxOption[];
  children?: React.ReactNode;
  onChange?: () => void;
};

export interface filterAlleyMessages {
  clear: string;
  thematiquesLabel: string;
  categoriesLabel: string;
  filtersLabel: string;
  dateMessages: {
    dateLabel: string;
    placeholder: string;
    dividerText: string;
    dateRangeText: string;
    before: string;
    after: string;
  };
  timeFrameDropdownMessages: ITimeFrameDropdownMessages;
}

interface ISearchFilterAlleyProps {
  dateRange: DateRangeISOString;
  messages: filterAlleyMessages;
  categoryOptions: CheckboxOption[];
  labelOptions: CheckboxOption[];
  baseUrl: string;
  redirectUrl: string;
  keyword: string;
  onClick?: () => void;
  setIsFilterLoading: Dispatch<SetStateAction<boolean>>;
}

export interface IDateRangePicker {
  messages: {
    dateLabel: string;
    placeholder: string;
    dividerText: string;
    dateRangeText: string;
    before: string;
    after: string;
  };
  dateRange: DateRangeISOString;
  dateSelectHandler: (from?: number, to?: number) => void;
  timeFrameDropdownMessages: ITimeFrameDropdownMessages;
}

export interface IDates {
  minValue?: Date;
  maxValue?: Date;
}

export interface ITimeFrameDropdownMessages {
  label: string;
  timeFrameDropdownPlaceholder: string;
  timeFrameOptions: {
    threeMonths: { label: string; sortOrder: number };
    sixMonths: { label: string; sortOrder: number };
    oneYear: { label: string; sortOrder: number };
    twoYears: { label: string; sortOrder: number };
    threeYears: { label: string; sortOrder: number };
    fourYears: { label: string; sortOrder: number };
    fiveYears: { label: string; sortOrder: number };
    tenYears: { label: string; sortOrder: number };
  };
}

const CheckboxList = (props: ICheckboxListProps) => {
  const { options, onChange, name } = props;
  return (
    <Flex gap="md" flexDirection="column">
      <Flex flexDirection="column" gap="md">
        {options?.map((option) => (
          <Checkbox
            automation-id={`search-checkbox-${name}-${option.value}`}
            value={option.value}
            key={option.value}
            name={name}
            checked={option.checked}
            onChange={onChange}
            disabled={!option.checked && option.disabled}
            width={"fit-content"}
          >
            {option.name}
          </Checkbox>
        ))}
      </Flex>
    </Flex>
  );
};

const buildThematiquesFilter = (props: IThematiquesFilterProps) => {
  const { thematiquesLabel, categoryOptions, onChange } = props;
  return (
    <ToggleStateProvider initialState={true}>
      {({ value: open, handler: handleClick }) => (
        <>
          <ListItem
            onClick={handleClick()}
            automation-id="search-categories-collapse-toggle"
          >
            <ListItemText primary={thematiquesLabel} />
            <ToggleFlipIcon icon="chevronUp" toggleState={open} />
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid pt="xl" pb="lg" rowGap="xl+2">
              <CheckboxList
                name={"categories"}
                options={categoryOptions}
                onChange={onChange}
              ></CheckboxList>
            </Grid>
          </Collapse>
        </>
      )}
    </ToggleStateProvider>
  );
};

const buildCategoriesFilter = (props: ICategoriesFilterProps) => {
  const { categoriesLabel, labelOptions, onChange } = props;
  return (
    <ToggleStateProvider initialState={true}>
      {({ value: open, handler: handleClick }) => (
        <>
          <ListItem
            onClick={handleClick()}
            automation-id="search-labels-collapse-toggle"
          >
            <ListItemText primary={categoriesLabel} />
            <ToggleFlipIcon icon="chevronUp" toggleState={open} />
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid pt="xl" pb="lg" rowGap="xl+2">
              <CheckboxList
                name={"labels"}
                options={labelOptions}
                onChange={onChange}
              ></CheckboxList>
            </Grid>
          </Collapse>
        </>
      )}
    </ToggleStateProvider>
  );
};

const renderTimeFrameDropdown = (messages: ITimeFrameDropdownMessages) => {
  // eslint-disable-next-line react/display-name
  return (props: TimeFrameDropdownProps) => {
    return (
      <TimeFrameDropDown
        {...props}
        label={messages.label}
        options={messages.timeFrameOptions}
        timeFrameDropdownPlaceholder={messages.timeFrameDropdownPlaceholder}
      />
    );
  };
};

const getTimeStampsFromDateRange = (dateRange: DateRangeISOString) => {
  return {
    from: dateRange.minValue
      ? new Date(dateRange.minValue).getTime()
      : undefined,
    to: dateRange.maxValue ? new Date(dateRange.maxValue).getTime() : undefined,
  };
};

const DateRangePicker = (props: IDateRangePicker) => {
  const { messages, dateRange, dateSelectHandler } = props;
  const onDateSelect = (change: DateRangeISOString) => {
    const timestamps = getTimeStampsFromDateRange(change);
    dateSelectHandler(timestamps.from, timestamps.to);
  };

  const localizeAfterText: ResolveRangeDateText = (data) => {
    return `${messages.after} ${data.localFormattedDate}`;
  };

  const localizeBeforeText: ResolveRangeDateText = (data) => {
    return `${messages.before} ${data.localFormattedDate}`;
  };

  return (
    <ToggleStateProvider initialState={true}>
      {({ value: open, handler: handleClick }) => (
        <>
          <ListItem
            onClick={handleClick()}
            automation-id="search-date-collapse-toggle"
          >
            <ListItemText primary={messages.dateLabel} />
            <ToggleFlipIcon icon="chevronUp" toggleState={open} />
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid pt="xl" pb="lg" rowGap="xl+2">
              <Flex gap="md" flexDirection="column">
                <DateRange
                  automation-id={"search-date-selector"}
                  onDateSelect={onDateSelect}
                  selectedDatesISOString={dateRange}
                  placeholder={messages.placeholder}
                  locale={"fr-FR"}
                  renderTimeFrameDropdown={renderTimeFrameDropdown(
                    props.timeFrameDropdownMessages
                  )}
                  dividerText={messages.dividerText}
                  dateRangeText={messages.dateRangeText}
                  isClearable={true}
                  resolveAfterText={localizeAfterText}
                  resolveBeforeText={localizeBeforeText}
                  dateRangePickerProps={{ placeholder: "JJ/MM/AAAA" }}
                  popoverProps={{ disableScrollLock: false }}
                />
              </Flex>
            </Grid>
          </Collapse>
        </>
      )}
    </ToggleStateProvider>
  );
};
export const SearchFilterAlley = (props: ISearchFilterAlleyProps) => {
  const {
    messages,
    categoryOptions,
    labelOptions,
    baseUrl,
    redirectUrl,
    keyword,
    onClick,
    setIsFilterLoading,
  } = props;
  const form = useRef<HTMLFormElement>(null);

  const router = useRouter();
  const pathName = usePathname();

  const updateURLHandler = () => {
    const timestamps = getTimeStampsFromDateRange(props.dateRange);
    updateURL(timestamps.from, timestamps.to);
  };

  const [isPending, startTransition] = useTransition();

  const updateURL = (from?: number, to?: number) => {
    const formData = new FormData(form.current as HTMLFormElement);
    const thematiques = formData.getAll("categories");
    const categories = formData.getAll("labels");
    const url = buildSearchURL({
      baseUrl: baseUrl,
      redirectUrl: redirectUrl,
      keyword: keyword || "",
      to: to,
      from: from,
      thematiques: thematiques as string[],
      categories: categories as string[],
    });
    trackEventApi(
      {
        event: ClientTrackingEvent.SEARCH_PERFORMED,
        searchCriteria: {
          keyword: keyword || "",
          thematiques: thematiques as string[],
          categories: categories as string[],
          toDate: to,
          fromDate: from,
        },
      },
      pathName
    );
    startTransition(() => {
      router.push(url, { scroll: false });
    });
  };

  const clearURL = () => {
    const url = new URL(redirectUrl, baseUrl);
    if (keyword) url.searchParams.set("keyword", keyword);
    startTransition(() => {
      router.push(url.href, { scroll: false });
    });
  };

  setIsFilterLoading(isPending);

  return (
    <form ref={form}>
      <Flex
        column
        relative
        automation-id="basic-filter-container"
        width="100%"
        minWidth="340px"
        bg="neutral.lighter"
        borderLeft="neutral.medium"
        borderRight="neutral.medium"
      >
        <div className="w-[100%] lg:hidden">
          <Flex justifyContent="end" flex="1">
            <Button
              automation-id={"search-close-button-mobile"}
              icon={<IconClose />}
              variant="ghostMod"
              justifyContent="end"
              onClick={() => {
                onClick ? onClick() : undefined;
              }}
            ></Button>
          </Flex>
        </div>
        <Flex
          column
          gap="xl"
          variant="scrollbar.container.lighter-bg"
          overflowY="scroll"
          pl="lg"
          className="h-[calc(100vh-120px)] lg:h-screen"
        >
          <Flex column gap="0px" p="lg" width="100%" mb="48px">
            <List variant="collapsiblePanel" gap="0px">
              <DateRangePicker
                messages={{
                  dateLabel: props.messages.dateMessages.dateLabel,
                  placeholder: props.messages.dateMessages.placeholder,
                  dividerText: props.messages.dateMessages.dividerText,
                  dateRangeText: props.messages.dateMessages.dateRangeText,
                  before: props.messages.dateMessages.before,
                  after: props.messages.dateMessages.after,
                }}
                dateRange={props.dateRange}
                dateSelectHandler={updateURL}
                timeFrameDropdownMessages={
                  props.messages.timeFrameDropdownMessages
                }
              />
              {buildThematiquesFilter({
                thematiquesLabel: messages.thematiquesLabel,
                categoryOptions: categoryOptions,
                onChange: updateURLHandler,
              })}
              {buildCategoriesFilter({
                categoriesLabel: messages.categoriesLabel,
                labelOptions: labelOptions,
                onChange: updateURLHandler,
              })}
            </List>
          </Flex>
        </Flex>
        <Flex
          bg="neutral.lighter"
          borderTop="neutral.medium"
          height="48px"
          position="sticky"
          bottom="0"
          right="0"
          left="0"
          alignItems="center"
          px="lg"
          zIndex="1"
        >
          <Flex justifyContent="end" flex="1">
            <Button
              automation-id={"search-button"}
              variant="secondary"
              justifyContent="end"
              onClick={clearURL}
              data-track-click
              data-track-details={trackingDetails({
                event: ClientTrackingEvent.NEWS_SEARCH_CLEARED,
              })}
            >
              {props.messages.clear}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </form>
  );
};
